import { forwardRef, lazy, memo, Suspense } from 'react';
import { Sized } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { IconVariant } from '@optimus-web/local-tokens';
import mapLegacyValues from './mapLegacyValues';
import { getIconSize, IconWrapper, StyledIcon } from './style';

export interface IconProps extends Sized {
    value: IconName;
    alt?: string;
    className?: string;
    variant?: IconVariant | 'default';
    forwardedRef?: React.Ref<HTMLSpanElement>;
}

const Icon = memo(({ alt, size = 'medium', value, variant = 'default', forwardedRef, ...other }: IconProps) => {
    const newValue = mapLegacyValues(value);
    const pixelSize = size === 'small' ? getIconSize('small') : getIconSize('medium');

    const SvgIcon = lazy(
        async () =>
            // eslint-disable-next-line no-unsanitized/method
            import(
                /* webpackChunkName: "icon_[request]" */ `@optimus-web/assets/src/icons/${newValue}_${pixelSize}.svg?inline`
            )
    );

    return (
        <IconWrapper ref={forwardedRef} data-test-icon={newValue} size={size} {...other}>
            <Suspense fallback={null}>
                <StyledIcon
                    as={SvgIcon}
                    size={size}
                    variant={variant}
                    aria-label={alt}
                    viewBox={`0 0 ${pixelSize} ${pixelSize}`}
                />
            </Suspense>
        </IconWrapper>
    );
});

export default forwardRef<HTMLSpanElement, IconProps>((props, ref) => <Icon {...props} forwardedRef={ref} />);
